import User from '@/models/User'
import * as MutationTypes from './mutation-types'

// A role name counts as "admin" when it is (or contains) Master — mirrors the
// User model's own admin derivation. No other JC Steele role grants admin.
function roleIsMaster(name) {
  return String(name || '').toLowerCase().indexOf('master') !== -1
}

// The real signed-in role as a single display string. The JWT role claim is
// usually a string, but can be an array when a user holds several roles;
// collapse an array to its admin-ish entry if present, else the first.
// Every role, as a clean string[]. Accepts the three shapes the role claim can actually arrive in:
// an ARRAY (2+ roles — one repeated ClaimTypes.Role claim each), a bare STRING (exactly 1 role), or
// a CSV string (how the Navigations table and the "view as user" payload store them). Trims, drops
// blanks, and de-dupes case-insensitively while preserving the original casing of the first hit.
// Splitting on "," is safe: no JC Steele role name contains a comma ("Sales All" uses a space).
// Handling the CSV shape too means this stays correct even if the token format ever changes.
function normalizeRoleList(raw) {
  if (raw === null || raw === undefined) return []
  const parts = (Array.isArray(raw) ? raw : String(raw).split(','))
    .map(s => String(s === null || s === undefined ? '' : s).trim())
    .filter(Boolean)
  const seen = Object.create(null)
  const out = []
  for (const p of parts) {
    const k = p.toLowerCase()
    if (!seen[k]) { seen[k] = true; out.push(p) }
  }
  return out
}

function realRoleNameOf(user) {
  if (!user) return ''
  const r = user.role
  if (Array.isArray(r)) {
    return r.find(roleIsMaster) || r[0] || ''
  }
  return r || ''
}


const state = {
  user: User.from(localStorage.token),
  // "View as role" — an ADMIN-ONLY UI preview. Holds the role NAME an admin has
  // chosen to preview the portal as (the options are sourced from the DB role
  // list via /user/GetRoleSelectItems). When this is a non-Master role AND the
  // signed-in user is really an admin, the `currentUser` getter below returns a
  // that-role-shaped view of the SAME identity, so every admin-gated affordance
  // hides and the admin sees what that role sees. The real JWT (used for API
  // auth in HttpClient) is untouched, so it is purely cosmetic and fully
  // reversible. Persisted across reloads; cleared on logout
  // (Logout.vue does localStorage.clear()) and on the LOGOUT mutation.
  viewAsRole: localStorage.viewAsRole || '',
  // "View as user" — an ADMIN-ONLY preview of a SPECIFIC user (id + name + role) from the selected company.
  // Setting it also sets viewAsRole (their role), so every role-gated affordance reflects them; the report
  // additionally loads THAT user's personal dashboard. Purely cosmetic (real JWT untouched). Persisted.
  viewAsUser: (function () { try { return JSON.parse(localStorage.viewAsUser || 'null'); } catch (e) { return null; } })(),
  loading: 0
}

const mutations = {
  [MutationTypes.LOGIN](state) {
    state.user = User.from(localStorage.token)
  },
  [MutationTypes.LOGOUT](state) {
    state.user = null
    state.viewAsRole = ''
    state.viewAsUser = null
  },
  [MutationTypes.SET_VIEW_AS_USER](state, user) {
    state.viewAsUser = user || null
    if (user) { localStorage.viewAsUser = JSON.stringify(user) } else { delete localStorage.viewAsUser }
  },
  [MutationTypes.SET_VIEW_AS_ROLE](state, role) {
    const r = role || ''
    state.viewAsRole = r
    if (r) {
      localStorage.viewAsRole = r
    } else {
      delete localStorage.viewAsRole
    }
  },
  [MutationTypes.START_LOADING](state) {
    state.loading++
  },
  [MutationTypes.FINISH_LOADING](state) {
    state.loading--
  }
}

const getters = {
  isLoggedIn: state => !!state.user,
  // Effective (possibly previewed) identity. When an admin is previewing a
  // non-Master role, return a that-role-shaped User built from the SAME
  // identity (admin stripped, role = the chosen role). Because every
  // isMasterUser / currentUser.admin gate in the app reads THIS getter, they
  // all flip off automatically. Otherwise return the real signed-in user.
  currentUser: state => {
    const u = state.user
    if (u && u.admin && state.viewAsRole && !roleIsMaster(state.viewAsRole)) {
      return new User({
        sub: u.id,
        admin: false,
        email: u.email,
        firstName: u.firstName,
        roles: state.viewAsRole
      })
    }
    return u
  },
  // The TRUE signed-in identity + role, ignoring the preview. Used by the
  // header role switcher so it stays visible (and knows the person is really
  // an admin) even while previewing another role.
  realUser: state => state.user,
  isRealAdmin: state => !!(state.user && state.user.admin),
  realRoleName: state => realRoleNameOf(state.user),
  // The role the switcher dropdown currently reflects: the chosen preview
  // role, else the real role.
  effectiveRoleName: state => {
    if (state.user && state.user.admin && state.viewAsRole) return state.viewAsRole
    return realRoleNameOf(state.user)
  },
  // EVERY role in effect, as an array — the multi-role counterpart of effectiveRoleName.
  //
  // Why this exists: GetToken emits one ClaimTypes.Role claim PER role, so the JWT claim decodes to
  // an ARRAY for anyone holding 2+ roles, and Report Access provisioning is ADDITIVE (Sales +
  // Manufacturing + Foundry is a real, common shape). realRoleNameOf() throws all but one away, and
  // the discarded ones are not chosen — Identity's join has no ORDER BY, so which role survives is
  // plan-dependent. That silently hid role-scoped menu links from multi-role users, and it makes an
  // exclusion rule ("Sales but NOT Manufacturing") impossible to express at all.
  //
  // effectiveRoleName is left byte-for-byte alone: ~30 call sites compare it with === "Customer" /
  // "Master" / "Sales", and widening its contract would quietly change all of them. New consumers
  // that care about the whole set use THIS getter instead.
  //
  // Preview precedence deliberately mirrors effectiveRoleName so the two can never disagree about
  // which identity is in effect:
  //   1. real admin + "view as user" -> that user's roles.
  //   2. real admin + "view as role" -> EXACTLY [that role]. A preview is never widened by the
  //      admin's own roles, which is what keeps "view as Sales" honest for a Master who is also Sales.
  //   3. otherwise -> every role on the real JWT.
  // Guards read state.user.admin (the REAL flag), so a non-admin cannot fabricate a preview by
  // editing localStorage.
  effectiveRoleNames: state => {
    const u = state.user
    if (u && u.admin) {
      const vu = state.viewAsUser
      if (vu) {
        const fromUser = normalizeRoleList(vu.roles !== undefined ? vu.roles : vu.role)
        if (fromUser.length) return fromUser
      }
      if (state.viewAsRole) return normalizeRoleList(state.viewAsRole)
    }
    return normalizeRoleList(u && u.role)
  },
  // Non-empty only while actively previewing a non-real (non-Master) role —
  // drives the header's amber "preview" styling.
  viewingAsRole: state => {
    const u = state.user
    if (u && u.admin && state.viewAsRole && !roleIsMaster(state.viewAsRole)) return state.viewAsRole
    return ''
  },
  // The specific user an admin is previewing as ({ id, name, role }), else null. Drives the report's
  // per-user data load (it fetches THIS user's saved dashboard) and the header "viewing as <name>" label.
  viewingAsUser: state => {
    const u = state.user
    if (u && u.admin && state.viewAsUser) return state.viewAsUser
    return null
  },
  loading: state => state.loading
}

const actions = {
  login({ commit }) {
    commit(MutationTypes.LOGIN)
  },

  logout({ commit }) {
    commit(MutationTypes.LOGOUT)
  },

  setViewAsRole({ commit }, role) {
    commit(MutationTypes.SET_VIEW_AS_USER, null)   // picking a role directly clears any user-preview
    commit(MutationTypes.SET_VIEW_AS_ROLE, role)
  },
  // Preview a specific user: sets the user AND their role (so role-gating reflects them). Pass null to clear.
  setViewAsUser({ commit }, user) {
    commit(MutationTypes.SET_VIEW_AS_USER, user || null)
    commit(MutationTypes.SET_VIEW_AS_ROLE, (user && user.role) ? user.role : '')
  }
}

export default {
  state,
  mutations,
  getters,
  actions
}
